import { ScrollText, ShieldCheck, Users } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { ORGUS_MARK_32, OrgusMark } from '@orgus/ui';
import { ORGUS_KEYLINES } from './keylines.ts';

/**
 * The brand side of the sign-in screen (wide screens only): the mark over its construction
 * keylines (the site's hero composition), the tagline, and facts that are true of this hub today (each one is
 * checked against docs/architecture/spec.md; keep it that way).
 *
 * Colours are tokens only. The panel scopes the dark theme to itself, so it reads as the calm
 * brand field in the light theme too; high contrast keeps its own tokens.
 */

export const BRAND_TAGLINE = 'Autonomy, under control.';
export const BRAND_LINE = 'The control plane for AI-native engineering. Every agent, every change, accounted for.';

export const TRUST_FACTS: ReadonlyArray<{ icon: LucideIcon; text: string }> = [
  { icon: ScrollText, text: 'Every action recorded against who took it, in a hash-chained audit log.' },
  { icon: ShieldCheck, text: 'Every command to a machine signed, and void after 30 seconds.' },
  { icon: Users, text: 'Roles decide who watches, who runs sessions and who manages people.' },
];

/**
 * The mark over its construction keylines, on the same 32-unit grid as the marketing site's hero
 * (keylines.ts). The mark is the Prism in its facet colours (ADR 0061), the same drawing as the
 * lockup above the form, so the screen never shows two marks.
 */
export function BrandKeep({ className }: { className?: string }) {
  return (
    <svg viewBox={ORGUS_KEYLINES.viewBox} className={className} aria-hidden focusable="false" data-brand="prism">
      <g fill="none" stroke={ORGUS_KEYLINES.colour} strokeWidth={ORGUS_KEYLINES.strokeWidth}>
        <path d={ORGUS_KEYLINES.axes} />
        <path d={ORGUS_KEYLINES.frame} opacity={ORGUS_KEYLINES.frameOpacity} />
      </g>
      <OrgusMark size={ORGUS_MARK_32.grid} decorative />
    </svg>
  );
}

export function SignInBrandPanel() {
  // Dark and light both get the dark brand field.
  const scoped = 'dark';
  return (
    <aside
      data-theme={scoped}
      aria-label="About Orgus"
      className="relative hidden h-full min-h-[640px] flex-col overflow-hidden border-l border-border bg-rail px-12 py-12 text-fg lg:flex xl:px-16"
    >
      {/* One column, one axis: the mark and the words share the same 480 px measure, and the
          mark sits on that column's centre line, not the panel's. */}
      <div className="relative flex w-full max-w-[480px] flex-1 flex-col">
        {/* Takes the height the words leave, so nothing is cut off on a short screen. */}
        <div className="relative my-4 min-h-[180px] flex-1">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative aspect-square h-full max-h-[440px] max-w-full">
              {/* One soft Sapphire wash behind the mark, the only glow in the system. */}
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-[20%]"
                style={{ backgroundImage: 'radial-gradient(closest-side, color-mix(in oklab, var(--accent) 20%, transparent), transparent)' }}
              />
              <BrandKeep className="absolute inset-0 size-full" />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <p className="text-3xl font-semibold text-fg">{BRAND_TAGLINE}</p>
            <p className="text-md text-fg-muted">{BRAND_LINE}</p>
          </div>
          <ul className="flex flex-col gap-3 border-t border-border pt-6">
            {TRUST_FACTS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3 text-base text-fg-muted">
                <Icon className="mt-0.5 size-4 shrink-0 text-fg-subtle" aria-hidden />
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}
